import React from "react";
import { 
  Sparkles, CheckCircle, Users, ArrowRight, ShieldCheck, Check, Search, HelpCircle
} from "lucide-react";
import { getFeatureBySlug, FeatureRecord } from "../../utils/featureRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";
import { 
  SeoSearchActionModule, 
  SeoWhyPageExistsSection, 
  SeoExampleResultsPreview, 
  SeoTrustSignalBlock, 
  SampleBusinessCard 
} from "./SeoUniversalPageEngine";

interface FeaturePageProps {
  featureSlug: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function FeaturePage({ featureSlug, onNavigate, onLogin = () => {}, currentUser }: FeaturePageProps) {
  const feat: FeatureRecord | null = getFeatureBySlug(featureSlug);

  if (!feat) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Feature Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The requested feature overview page does not exist.</p>
        <button
          onClick={() => onNavigate("/features")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View All Features
        </button>
      </div>
    );
  }

  const currentRoute = `/features/${feat.slug}`;

  useSeoMetadata({
    title: `${feat.name} - Feature Overview | BizNearYou`,
    description: feat.shortDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": feat.name,
      "description": feat.shortDescription,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  const featureSampleCards: SampleBusinessCard[] = [
    {
      name: `Commercial Technology Partners (${feat.name})`,
      category: "Feature Intelligence",
      location: "Verified Regional Index",
      signalTier: "high",
      signalBadgeText: "High Signal",
      reason: `Utilizing ${feat.name.toLowerCase()} technology to spot active business opportunities.`,
      freshness: "Verified 1 day ago",
      verifiability: "Verified Public Web Data"
    },
    {
      name: `Regional Operations Hub`,
      category: "Commercial Directory",
      location: "Metro Area",
      signalTier: "possible",
      signalBadgeText: "Possible Signal",
      reason: `Verified decision maker contact intake active and indexed.`,
      freshness: "Verified 3 days ago",
      verifiability: "Public Business Registry"
    }
  ];

  return (
    <div className="w-full">
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <SeoBreadcrumbs
          items={[
            { label: "Features", path: "/features" },
            { label: feat.name, path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Hero Banner */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-150 mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Platform Feature Overview</span>
            </span>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              {feat.name}
            </h1>

            <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6">
              {feat.fullDescription}
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="/find-jobs"
                onClick={(e) => handleLinkClick(e, "/find-jobs")}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition flex items-center gap-2 cursor-pointer shadow-sm"
              >
                <Search className="w-4 h-4" />
                <span>Try {feat.name} Now</span>
              </a>
              <a
                href="/how-it-works"
                onClick={(e) => handleLinkClick(e, "/how-it-works")}
                className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition flex items-center gap-2 cursor-pointer"
              >
                <span>How It Works Guide</span>
              </a>
            </div>
          </div>
        </div>

        {/* Interactive Search Module */}
        <SeoSearchActionModule
          defaultLocation=""
          defaultKeyword={feat.name}
          onNavigate={onNavigate}
        />

        {/* Results Preview */}
        <SeoExampleResultsPreview
          cards={featureSampleCards}
          locationName={feat.name}
          onNavigate={onNavigate}
          onLogin={onLogin}
        />

        {/* Why Page Exists */}
        <SeoWhyPageExistsSection
          locationName={feat.name}
          contextDescription={`Discover how ${feat.name} simplifies commercial sourcing and owner outreach.`}
        />

        {/* 2-Col Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Main (2 cols) */}
          <div className="lg:col-span-2 space-y-8">
            {/* How It Works Steps */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4">How {feat.name} Works</h2>
              <div className="space-y-4">
                {feat.howItWorksSteps.map((step, i) => (
                  <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-150 flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-xs font-bold text-slate-900 mb-1">{step.title}</h3>
                      <p className="text-xs text-slate-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Who It Helps */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                <span>Who This Feature Helps</span>
              </h2>
              <ul className="space-y-2.5">
                {feat.whoItHelps.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs text-slate-700">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar (1 col) */}
          <div className="space-y-6">
            {/* Free vs Pro Breakdown */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Tier Availability</h3>
              <div className="space-y-4 text-xs">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <span className="font-bold text-slate-800 uppercase tracking-wider block mb-1 text-[10px]">Free Tier</span>
                  <p className="text-slate-600">{feat.tierAvailability.freeTier}</p>
                </div>
                <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200">
                  <span className="font-bold text-indigo-900 uppercase tracking-wider block mb-1 text-[10px]">Pro Tier</span>
                  <p className="text-indigo-950">{feat.tierAvailability.proTier}</p>
                </div>
              </div>
            </div>

            {/* Key Benefits */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Key Benefits</h3>
              <ul className="space-y-2 text-xs text-slate-700">
                {feat.keyBenefits.map((b, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
